import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, ShieldCheck, ArrowLeft } from "lucide-react";

// Inline, theme-driven two-factor password reset — rendered in place of the login form
// on each auth page and styled to match that page (pass inputClass / btnClass / btnStyle / accents).
// Step 1: enter email -> we email a 6-digit code (and learn if the account has MFA).
// Step 2: emailed code + (if MFA on) authenticator/recovery code + new password x2.
export function ForgotPassword({
  client, base, onBack, title = "Reset your password",
  inputClass = "w-full rounded-xl px-4 h-12 text-[14px] text-white bg-white/[0.04] border border-white/10 outline-none transition-colors focus:border-cyan-400/70 placeholder:text-white/30",
  btnClass = "w-full h-12 rounded-xl text-[14px] font-semibold text-[#05070d] flex items-center justify-center gap-2 disabled:opacity-60",
  btnStyle = { background: "linear-gradient(90deg,#22d3ee,#8b5cf6)" },
  accentClass = "text-cyan-300 hover:text-cyan-200",
  mutedClass = "text-white/55",
  headingClass = "font-editorial text-3xl tracking-tight",
}) {
  const [mode, setMode] = useState("email"); // email | reset
  const [email, setEmail] = useState("");
  const [mfaOn, setMfaOn] = useState(false);
  const [otp, setOtp] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [recovery, setRecovery] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [np, setNp] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);

  const submitEmail = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`${base}/forgot-password`, { email: email.trim() });
      setMfaOn(!!data.mfa);
      setMode("reset");
      toast.success("If that account exists, we've emailed a 6-digit code.");
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };

  const submitReset = async () => {
    if (np.a !== np.b) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const payload = { email: email.trim(), code: otp.trim(), password: np.a };
      if (mfaOn) { if (useRecovery) payload.recovery_code = recovery.trim(); else payload.mfa_code = mfaCode.trim(); }
      await client.post(`${base}/reset-password-code`, payload);
      toast.success("Password updated — you can sign in now");
      onBack();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && typeof d === "object" ? d.message : d) || "Could not reset password");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="forgot-panel">
      <div>
        <h2 className={headingClass}>{title}</h2>
        <p className={`mt-1.5 text-[13px] ${mutedClass}`}>
          {mode === "email"
            ? "Enter your account email and we'll send you a 6-digit reset code."
            : <>We emailed a 6-digit code to <span className="opacity-90 font-medium">{email}</span>.{mfaOn ? " Because this account uses two-factor authentication, also enter a code from your authenticator app." : ""} Then choose a new password.</>}
        </p>
      </div>

      {mode === "email" && (
        <div className="space-y-3" data-testid="forgot-email-step">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="forgot-email" className={inputClass} autoFocus />
          <button type="button" disabled={busy || !email} onClick={submitEmail} data-testid="forgot-email-submit" className={btnClass} style={btnStyle}><Mail size={15} /> {busy ? "Please wait…" : "Send reset code"}</button>
        </div>
      )}

      {mode === "reset" && (
        <div className="space-y-3" data-testid="forgot-reset-step">
          <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="6-digit email code" data-testid="forgot-otp" className={`${inputClass} tracking-widest text-center`} />
          {mfaOn && (
            <div className="space-y-2" data-testid="forgot-mfa-block">
              {!useRecovery ? (
                <input value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="6-digit authenticator code" data-testid="forgot-mfa-code" className={`${inputClass} tracking-widest text-center`} />
              ) : (
                <input value={recovery} onChange={(e) => setRecovery(e.target.value)} placeholder="Recovery code (XXXX-XXXX-XXXX)" data-testid="forgot-recovery-code" className={`${inputClass} text-center`} />
              )}
              <button type="button" onClick={() => setUseRecovery((v) => !v)} data-testid="forgot-toggle-recovery" className={`text-[12.5px] ${accentClass}`}>{useRecovery ? "Use an authenticator code instead" : "Can't use your authenticator? Use a recovery code"}</button>
            </div>
          )}
          <input type="password" value={np.a} onChange={(e) => setNp({ ...np, a: e.target.value })} placeholder="New password (min 12 chars, symbol, number, capital)" data-testid="forgot-newpw" className={inputClass} />
          <input type="password" value={np.b} onChange={(e) => setNp({ ...np, b: e.target.value })} placeholder="Confirm new password" data-testid="forgot-newpw2" className={inputClass} />
          <button type="button" disabled={busy || otp.length !== 6 || !np.a || (mfaOn && (useRecovery ? !recovery : mfaCode.length !== 6))} onClick={submitReset} data-testid="forgot-reset-submit" className={btnClass} style={btnStyle}><ShieldCheck size={15} /> {busy ? "Please wait…" : "Reset password"}</button>
        </div>
      )}

      <button type="button" onClick={onBack} data-testid="forgot-back" className={`w-full flex items-center justify-center gap-1.5 text-[12.5px] ${accentClass}`}><ArrowLeft size={13} /> Back to sign in</button>
    </div>
  );
}
